import { useEffect, useState } from "react";
import { type ComponentProps } from "react";

import { useNavigate, useSearchParams } from "@remix-run/react";
import { usePrevious } from "@uidotdev/usehooks";
import { useActorRef } from "@xstate/react";
import { assign } from "xstate";

import { Button } from "~/components/ui";
import { BackArrowIcon } from "~/icons";
import { cn } from "~/lib/utils";
import { analytics } from "~/utils/analytics.client";

export function useQuestionnaireMachine(machine) {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [isInitialized, setIsInitialized] = useState(false);

    const actorRef = useActorRef(
        machine.provide({
            actions: {
                "packages.navigate": (_, { pathname, search }) => {
                    navigate({
                        pathname,
                        search,
                    });
                },
                "dialog.open": ({ context }) => {
                    searchParams.set("contactUs", context.history);
                    setSearchParams(searchParams);
                },
                "searchParams.update": assign({
                    previousState: () => {
                        return actorRef.getSnapshot().value;
                    },
                    history: ({ context, event }) => {
                        // history allows us to re-play the events when reloading the page
                        const newHistory = [...context.history];
                        if (event.type === "BACK") {
                            newHistory.pop();
                        } else {
                            newHistory.push(event.type);
                        }
                        const params = new URLSearchParams(searchParams);
                        if (newHistory.length > 0) {
                            params.set("events", newHistory.join(","));
                        } else {
                            params.delete("events");
                        }
                        setSearchParams(params, { preventScrollReset: true });
                        return newHistory;
                    },
                }),
            },
        }),
        {
            inspect: (inspEvent) => {
                if (inspEvent.type === "@xstate.snapshot") {
                    const { event } = inspEvent;
                    if (event.analytics) {
                        analytics.track.apply(analytics.track, event.analytics);
                    }
                }
            },
        },
    );

    const send = actorRef.send;

    const previousSearchParams = usePrevious(searchParams);
    useEffect(() => {
        // FIXME needs to go back when pressed "back button" in the browser
        // contactUs dialog opens with search param: ?contactUs=...
        // this dialog state is managed outside of the state machine so we need this hook, and can't rely on `exit` of the contact us state
        // we manage this by triggering a "back" event on the machine and the dialog will closed.
        if (previousSearchParams && previousSearchParams.get("contactUs") && !searchParams.get("contactUs")) {
            send({ type: "BACK" });
        }
    }, [searchParams, previousSearchParams, send]);

    useEffect(() => {
        // ideally we would want to load the machine in the loader and send getPersistedSnapshot so SSR would have the correct state
        const events = (searchParams.get("events") || "").split(",");
        events.forEach((type) => send({ type }));

        setIsInitialized(true);
        const listener = () => {
            send({ type: "BACK" });
        };
        window.addEventListener("popstate", listener);
        return () => window.removeEventListener("popstate", listener);
        // ignore searchParams because we only want to re-play events on mount/page load
        // eslint-disable-next-line
    }, [actorRef, send]);

    return { isInitialized, snapshot: actorRef.getSnapshot(), send };
}

export function PreviousStep({ onClick }: ComponentProps<"button">) {
    // TODO: Use button component
    return (
        <Button onClick={onClick} className="self-start" variant="action">
            <BackArrowIcon />
            Previous step
        </Button>
    );
}
export function MachineButton({
    state,
    send,
    context,
    className,
    isAffirmative = true,
    label,
    disabled = false,
    onClick,
}: {
    state: string;
    send: any;
    context?: any;
    className?: string;
    isAffirmative?: boolean;
    label: string;
    disabled?: boolean;
    onClick: () => void;
}) {
    return (
        <Button
            variant={!isAffirmative ? "outline" : disabled ? "disabled" : "default"}
            onClick={() => {
                onClick && onClick();
                send({ type: state, context });
            }}
            disabled={disabled}
            className={cn("w-1/3", className)}
        >
            {label}
        </Button>
    );
}

// this might be a hack but it get the job done
// look into each state to see if they can transition to `_from`
export function generateBackTransitionsFrom(states, _from: string) {
    const transitions = [];
    Object.keys(states).forEach((target) => {
        const state = states[target];
        const needsTransition = !!Object.keys(state.on).find((event) => {
            const { target } = state.on[event];
            return target === _from;
        });
        if (needsTransition) {
            transitions.push({
                guard: ({ context: { previousState } }) => previousState === target,
                target,
            });
        }
    });
    return transitions;
}
